/**
 * Anschluss an die Datenhaltung von Symphenia (AP-22-D).
 *
 * Die Engine spricht die Sprache des Prototyps (`kommt`, `geht`, `abwesenheitId`
 * als Zahl …). Gespeichert wird in Symphenia englisch und mit UUIDs. Die
 * Übersetzung steht **hier und nur hier**: Das Backend rechnet damit Vormonate
 * und später die Auswertung, das Frontend rechnet damit live im Monatsraster.
 * Stünde sie zweimal, wäre das die Stelle, an der Browser und Server
 * verschiedene Zahlen zeigen, ohne dass eine Prüfung es merkt.
 *
 * Rein wie der Rest des Kerns: keine Datenbank, kein Netz.
 */

import { berechneMonat, periodeUebertrag } from './engine'
import { mitEntlohnung, type Entlohnung } from './entlohnung'
import { parseKonfig } from './konfig'
import { kvVorlage } from './kv'
import { dauerStunden, rundeStunden } from './zeit'
import type {
  AbwesenheitInfo,
  EngineKonfig,
  MitarbeiterKontext,
  MonatsErgebnis,
  PeriodeVorher,
  Sollzeit,
  TagEingabe,
} from './types'

/** Eine gespeicherte Tageszeile (`payroll_day_entries`), Datum als ISO-Text. */
export interface Tageszeile {
  date: string
  startTime?: string | null
  endTime?: string | null
  breakMinutes?: number | null
  grossOverride?: number | null
  netOverride?: number | null
  travelHours?: number | null
  departure?: string | null
  returnTime?: string | null
  km?: number | null
  isDriver?: boolean
  overnight?: boolean
  absenceTypeId?: string | null
  absenceScope?: 'FULL' | 'HALF' | null
  /** Fehlzeiten des Tages in Stunden (AP-22 Teil 4); gelten statt `absenceTypeId`/`absenceScope`. */
  absences?: { absenceTypeId: string; hours: number }[] | null
  appointment?: string | null
  appointmentHours?: number | null
  /**
   * Tageseingaben nach Schlüssel (AP-22 Teil 6, K3): Stunden je Zulage, `true` bei Zulagen mit
   * Kennzeichen, dazu die beiden Kennzeichen `rueckruf` und `verpflegung`.
   */
  allowances?: Record<string, number | boolean> | null
}

/** Die Kennzeichen in `allowances`, die keine Zulage sind, sondern einen Satz ändern (KV-8). */
export const TAGESKENNZEICHEN = ['rueckruf', 'verpflegung'] as const

/** Die Felder einer Tageszeile, die etwas eintragen — alles außer dem Datum. */
export const TAGESZEILE_FELDER = [
  'startTime',
  'endTime',
  'breakMinutes',
  'grossOverride',
  'netOverride',
  'travelHours',
  'departure',
  'returnTime',
  'km',
  'isDriver',
  'overnight',
  'absenceTypeId',
  'absenceScope',
  'absences',
  'appointment',
  'appointmentHours',
  'allowances',
] as const satisfies readonly (keyof Tageszeile)[]

/**
 * Trägt die Zeile nichts ein? Dann gibt es sie nicht — ein leerer Tag hat keine
 * Zeile. `absenceScope` allein zählt nicht: Ohne Art ist der Umfang bedeutungslos.
 */
export function istLeereTageszeile(z: Omit<Tageszeile, 'date'>): boolean {
  return TAGESZEILE_FELDER.every((f) => {
    if (f === 'absenceScope') return true
    const w = z[f]
    if (Array.isArray(w)) return w.length === 0
    // Tageseingaben der Zulagen: leer, wenn nichts darin etwas einträgt (0 Stunden, nicht angehakt).
    if (w !== null && typeof w === 'object') return Object.values(w).every((v) => !v)
    return w === null || w === undefined || w === false || (typeof w === 'string' && w.trim() === '')
  })
}

function text(w: string | null | undefined): string | null {
  return w == null || w.trim() === '' ? null : w
}

/** Gespeicherte Zeile → Eingabe der Engine. */
export function tageszeileZuEingabe(z: Tageszeile): TagEingabe {
  return {
    datum: z.date,
    kommt: text(z.startTime),
    geht: text(z.endTime),
    gesamtOverride: z.grossOverride ?? null,
    nazOverride: z.netOverride ?? null,
    pauseMin: z.breakMinutes ?? null,
    reisezeitStd: z.travelHours ?? null,
    abfahrt: text(z.departure),
    rueckfahrt: text(z.returnTime),
    km: z.km ?? null,
    lenker: z.isDriver === true,
    naechtigung: z.overnight === true,
    abwesenheitId: z.absenceTypeId ?? null,
    abwesenheitUmfang: z.absenceTypeId ? (z.absenceScope === 'HALF' ? 'halb' : 'ganz') : null,
    abwesenheiten: z.absences?.length ? z.absences.map((a) => ({ id: a.absenceTypeId, stunden: a.hours })) : null,
    terminTitel: text(z.appointment),
    terminStunden: z.appointmentHours ?? null,
    rueckruf: z.allowances?.rueckruf === true,
    verpflegungGestellt: z.allowances?.verpflegung === true,
    zulagen: z.allowances ?? null,
  }
}

/**
 * Die Pause, mit der eine Tageszeile **vorbelegt** wird (AP-22 Teil 5) — in Minuten, oder
 * `null`, wenn nichts vorzubelegen ist.
 *
 * Vorbelegt wird, wo die automatische Pausenregel ohnehin abzöge: Die Regel ist an, im Feld
 * steht nichts, und die Gesamtzeit liegt über der Schwelle. Der Wert steht danach in der
 * Zeile und rechnet wie jede erfasste Pause — die Rechnung ändert sich dadurch nicht, sie
 * wird nur sichtbar. Eine erfasste Pause (auch 0) bleibt, wie sie ist; mit einer Netto-Zeit
 * von Hand ist die Pause ohne Bedeutung.
 */
export function standardPauseMinuten(z: Omit<Tageszeile, 'date'>, konfig: EngineKonfig): number | null {
  if (!konfig.pauseAktiv || z.breakMinutes != null || z.netOverride != null) return null
  const gesamt =
    z.grossOverride ??
    rundeStunden(dauerStunden(text(z.startTime), text(z.endTime)), konfig.rundungSchrittMin, konfig.rundungAktiv)
  const minuten = Math.round(konfig.pauseDauerStd * 60)
  return gesamt > konfig.pauseSchwelleStd && minuten > 0 ? minuten : null
}

/** Das Arbeitszeitmodell, wie es für einen Monat gilt (`work_schedules` + Tage). */
export interface Arbeitszeitmodell {
  weeklyHours: number
  employment: 'FULL_TIME' | 'PART_TIME'
  cycleWeeks: number
  /** Anker-Montag des Zyklus, nur bei `cycleWeeks >= 2` nötig. */
  cycleAnchor: string | null
  days: { week: number; weekday: number; targetHours: number }[]
  /** Stundenrahmen statt Tages-Soll (AP-22 Teil 7); fehlt oder `null` = Tages-Soll. */
  hoursFrame?: 'WEEK' | 'MONTH' | null
  /** Stunden je Woche bzw. Monat des Rahmens. */
  frameHours?: number | null
  /** false = nur Kontrolle, keine Mehrzeit. Vorgabe true. */
  frameOvertime?: boolean
}

/**
 * Anker, wenn keiner gepflegt ist. Bei einem einwöchigen Modell ist er
 * bedeutungslos — jede Woche ist Woche 1. Ein Zyklus ohne Anker wird beim
 * Speichern abgewiesen (AP-22-B), kommt hier also nicht an.
 */
const ANKER_VORGABE = '2024-01-01'

/**
 * Modell → Kontext der Engine. Ohne Modell rechnet die Engine mit Soll 0 an
 * jedem Tag — die Maske sagt das dazu, statt still falsche Mehrstunden zu zeigen.
 */
export function kontextAusModell(
  modell: Arbeitszeitmodell | null,
  extra: { vortagNaechtigung?: boolean; periodeVorher?: PeriodeVorher; vorwoche?: TagEingabe[] } = {},
): MitarbeiterKontext {
  const sollzeit: Sollzeit[] = (modell?.days ?? []).map((d) => ({
    woche: d.week,
    wochentag: d.weekday,
    sollStunden: d.targetHours,
  }))
  return {
    zyklusWochen: modell?.cycleWeeks ?? 1,
    ankerDatum: modell?.cycleAnchor ?? ANKER_VORGABE,
    sollzeit,
    wochenstunden: modell?.weeklyHours,
    beschaeftigung: modell ? (modell.employment === 'PART_TIME' ? 'teilzeit' : 'vollzeit') : undefined,
    vortagNaechtigung: extra.vortagNaechtigung,
    periodeVorher: extra.periodeVorher,
    vorwoche: extra.vorwoche,
    ...(modell?.hoursFrame
      ? {
          rahmen: {
            zeitraum: modell.hoursFrame === 'MONTH' ? ('monat' as const) : ('woche' as const),
            stunden: modell.frameHours ?? 0,
            mehrzeit: modell.frameOvertime !== false,
          },
        }
      : {}),
  }
}

/** Eine Fehlzeit-Art der Lohnverrechnung (`payroll_absence_types`). */
export interface Fehlzeitart {
  id: string
  code: string
  paid: boolean
  countsAsTarget: boolean
  timeCompensation: boolean
}

export function abwesenheitenAusArten(arten: Fehlzeitart[]): Map<string, AbwesenheitInfo> {
  return new Map(
    arten.map((a) => [
      a.id,
      {
        id: a.id,
        kuerzel: a.code,
        bezahlt: a.paid,
        alsSoll: a.countsAsTarget,
        zeitausgleich: a.timeCompensation,
      },
    ]),
  )
}

/** Alles, was ein Monat zum Rechnen braucht — so, wie der Server es liefert. */
export interface Monatseingang {
  jahr: number
  monat: number
  zeilen: Tageszeile[]
  /** null = für den Monat ist kein Arbeitszeitmodell gültig. */
  modell: Arbeitszeitmodell | null
  /** Aufgelöste Einstellungen zum Stichtag (`aufloesenEinstellungen`). */
  werte: Record<string, string>
  /** Gesetzliche Feiertage des Monats. */
  feiertage: { date: string; name: string }[]
  /** Alle Arten, auch weich gelöschte — ein alter Tag behält seine Art. */
  arten: Fehlzeitart[]
  vortagNaechtigung?: boolean
  /** Übertrag der Vormonate derselben Periode (nur Modus „monate"); JSON liefert `null`. */
  periodeVorher?: PeriodeVorher | null
  /**
   * Der Vormonat ab dem Montag der ersten Woche (AP-22 Teil 5): seine Tageszeilen und
   * seine gesetzlichen Feiertage — damit Mehrarbeit und Überstunden je Kalenderwoche
   * gelten. `feiertage` oben bleiben die des Monats. Fehlt bei Monaten, die vor Teil 5
   * eingefroren wurden; dann beginnt die erste Woche mit dem Monatsersten.
   */
  vorwoche?: { zeilen: Tageszeile[]; feiertage: { date: string; name: string }[] } | null
  /** Entlohnungsart des Monats (AP-22 Teil 3); fehlt oder `null` = Standard des Betriebs. */
  entlohnung?: Entlohnung | null
  /**
   * Schlüssel der KV-Vorlage (AP-22 Teil 6): Ihr Zulagenkatalog sagt der Engine, welche
   * Zulagen es gibt. Fehlt bei Monaten, die davor eingefroren wurden — dann gilt die
   * Standardvorlage, und weil deren Schalter in den eingefrorenen Werten fehlen, keine Zulage.
   */
  vorlage?: string | null
}

/** Der eine Rechenweg für Server und Browser. */
export function rechneMonat(e: Monatseingang): MonatsErgebnis {
  return mitEntlohnung(rechneStunden(e), e.entlohnung)
}

/** Die Stundenrechnung der Engine — ohne den Schritt der Entlohnungsart. */
function rechneStunden(e: Monatseingang): MonatsErgebnis {
  return berechneMonat(
    e.jahr,
    e.monat,
    e.zeilen.map(tageszeileZuEingabe),
    kontextAusModell(e.modell, {
      vortagNaechtigung: e.vortagNaechtigung,
      periodeVorher: e.periodeVorher ?? undefined,
      vorwoche: e.vorwoche?.zeilen.map(tageszeileZuEingabe),
    }),
    parseKonfig(e.werte, kvVorlage(e.vorlage).zulagen),
    new Map([...e.feiertage, ...(e.vorwoche?.feiertage ?? [])].map((f) => [f.date, f.name])),
    abwesenheitenAusArten(e.arten),
  )
}

/**
 * Übertrag der Vormonate für den Modus „alle x Monate": die Monate ab
 * Periodenbeginn bis vor `monat`, jeder mit dem Übertrag seiner eigenen
 * Vormonate gerechnet — genau wie der Prototyp (`vormonateUebertrag`).
 *
 * `vormonate` in aufsteigender Reihenfolge, jeder schon ohne `periodeVorher`.
 */
export function uebertragAusVormonaten(vormonate: Omit<Monatseingang, 'periodeVorher'>[]): PeriodeVorher | undefined {
  if (vormonate.length === 0) return undefined
  const ergebnisse: MonatsErgebnis[] = []
  for (const m of vormonate) {
    // Der Übertrag rechnet mit den geleisteten Stunden — die Pauschale ändert daran nichts.
    ergebnisse.push(rechneStunden({ ...m, vortagNaechtigung: false, periodeVorher: periodeUebertrag(ergebnisse) }))
  }
  return periodeUebertrag(ergebnisse)
}
